import React, { useState, useEffect } from 'react';
import { UpdatePlanRequest } from '../services/planService';
import { Plan } from '../models/Plan';

interface EditPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (planId: string, data: UpdatePlanRequest) => Promise<void>;
  plan: Plan | null;
  loading?: boolean;
}

export const EditPlanModal: React.FC<EditPlanModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  plan,
  loading = false,
}) => {
  const [formData, setFormData] = useState<UpdatePlanRequest>({
    amount: undefined,
    currency: undefined,
    features: undefined,
    isActive: undefined,
    description: undefined,
  });
  const [amountInput, setAmountInput] = useState<string>('');
  const [descriptionInput, setDescriptionInput] = useState<string>('');
  const [currencyInput, setCurrencyInput] = useState<string>('');
  const [featuresInput, setFeaturesInput] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when plan changes
  useEffect(() => {
    if (plan && isOpen) {
      setFormData({
        amount: undefined,
        currency: undefined,
        features: undefined,
        isActive: undefined,
        description: undefined,
      });
      setAmountInput('');
      setDescriptionInput('');
      setCurrencyInput('');
      setFeaturesInput('');
      setErrors({});
    }
  }, [plan, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // At least one field must be provided
    if (formData.amount === undefined && formData.isActive === undefined && formData.description === undefined && formData.currency === undefined && formData.features === undefined) {
      newErrors.general = 'Please update at least one field';
    }

    if (formData.amount !== undefined && formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (formData.description !== undefined && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    if (formData.currency !== undefined && !/^[A-Z]{3}$/.test(formData.currency)) {
      newErrors.currency = 'Currency must be a 3-letter ISO code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!plan) return;
    
    if (!validateForm()) {
      return;
    }

    // Only include fields that have been changed
    const updateData: UpdatePlanRequest = {};
    if (formData.amount !== undefined) {
      updateData.amount = formData.amount;
    }
    if (formData.isActive !== undefined) {
      updateData.isActive = formData.isActive;
    }
    if (formData.description !== undefined) {
      updateData.description = formData.description;
    }
    if (formData.currency !== undefined) {
      updateData.currency = formData.currency;
    }
    if (formData.features !== undefined) {
      updateData.features = formData.features.map(feature => feature.trim()).filter(Boolean);
    }

    try {
      await onSubmit(plan.planId, updateData);
      handleClose();
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  const handleClose = () => {
    setFormData({
      amount: undefined,
      currency: undefined,
      features: undefined,
      isActive: undefined,
      description: undefined,
    });
    setAmountInput('');
    setDescriptionInput('');
    setCurrencyInput('');
    setFeaturesInput('');
    setErrors({});
    onClose();
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setCurrencyInput(value);
    setFormData(prev => ({ ...prev, currency: value || undefined }));
    setErrors(prev => ({ ...prev, currency: '', general: '' }));
  };

  const handleFeaturesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setFeaturesInput(value);
    setFormData(prev => ({ ...prev, features: value.split('\n') }));
    setErrors(prev => ({ ...prev, general: '' }));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmountInput(value);
    // Only update formData if there's a valid number
    if (value === '' || value === '.') {
      setFormData(prev => ({ ...prev, amount: undefined }));
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setFormData(prev => ({ ...prev, amount: numValue }));
      }
    }
    // Clear error when user starts typing
    if (errors.amount) {
      setErrors(prev => ({ ...prev, amount: '' }));
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isActive = e.target.checked;
    setFormData(prev => ({ ...prev, isActive }));
    // Clear error when user changes status
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDescriptionInput(value);
    setFormData(prev => ({ ...prev, description: value }));
    // Clear error when user starts typing
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: '' }));
    }
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
  };

  if (!isOpen || !plan) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md sm:max-w-lg max-h-[95vh] overflow-y-auto p-4 sm:p-5 border shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Edit Plan: {plan.planName}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Current Plan Info */}
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Current Amount:</span> {plan.currency} {(plan.amount || 0).toFixed(2)}
            </p>
            <p className="text-sm text-blue-800">
              <span className="font-medium">Current Status:</span>{' '}
              <span className={`font-semibold ${plan.isActive ? 'text-green-700' : 'text-gray-700'}`}>
                {plan.isActive ? 'Active' : 'Inactive'}
              </span>
            </p>
            <p className="text-sm text-blue-800">
              <span className="font-medium">Current Description:</span> {plan.description || 'N/A'}
            </p>
            <p className="text-sm text-blue-800">
              <span className="font-medium">Current Features:</span> {(plan.features || []).length > 0 ? plan.features.join(', ') : 'None'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.general && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{errors.general}</p>
              </div>
            )}

            {/* Amount Update */}
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                New Amount
              </label>
              <input
                type="number"
                id="amount"
                value={amountInput}
                onChange={handleAmountChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.amount ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={`Current: ${plan.currency} ${(plan.amount || 0).toFixed(2)}`}
                min="0.01"
                step="0.01"
                disabled={loading}
              />
              {errors.amount && (
                <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Leave empty to keep current amount</p>
            </div>

            {/* Currency Update */}
            <div>
              <label htmlFor="editCurrency" className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <input
                type="text"
                id="editCurrency"
                value={currencyInput}
                onChange={handleCurrencyChange}
                maxLength={3}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.currency ? 'border-red-500' : 'border-gray-300'}`}
                placeholder={`Current: ${plan.currency}`}
                disabled={loading}
              />
              {errors.currency && <p className="mt-1 text-sm text-red-600">{errors.currency}</p>}
              <p className="mt-1 text-xs text-gray-500">Leave empty to keep the current currency.</p>
            </div>

            {/* Features Update */}
            <div>
              <label htmlFor="editFeatures" className="block text-sm font-medium text-gray-700 mb-1">Features (one per line)</label>
              <textarea
                id="editFeatures"
                value={featuresInput}
                onChange={handleFeaturesChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={(plan.features || []).length > 0 ? plan.features.join('\n') : 'No current features'}
                rows={3}
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">Leave untouched to keep current features; clear an edited value to remove all features.</p>
            </div>

            {/* Description Update */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={descriptionInput}
                onChange={handleDescriptionChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={plan.description ? `Current: ${plan.description}` : 'Current: N/A'}
                rows={4}
                maxLength={500}
                disabled={loading}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {descriptionInput.length}/500 characters. Leave empty to keep current description.
              </p>
            </div>

            {/* Status Toggle */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive !== undefined ? formData.isActive : plan.isActive}
                  onChange={handleStatusChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={loading}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Plan Status: <span className={(formData.isActive !== undefined ? formData.isActive : plan.isActive)
                    ? 'text-green-700 font-semibold'
                    : 'text-gray-700 font-semibold'
                  }>
                    {(formData.isActive !== undefined ? formData.isActive : plan.isActive) ? 'Active' : 'Inactive'}
                  </span>
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">
                {formData.isActive !== undefined 
                  ? (formData.isActive ? 'Status will be enabled' : 'Status will be disabled')
                  : (plan.isActive ? 'Toggle to disable this plan' : 'Toggle to enable this plan')
                }
              </p>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </span>
                ) : (
                  'Update Plan'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
